/**
 * OpsRetrievalModal
 *
 * Thin orchestrator: holds criteria-form state and delegates all preview/launch
 * logic to useOpsRetrievalJob. JSX is split across:
 *   - OpsRetrievalCriteriaPanel  (Panel 1: search form)
 *   - OpsRetrievalPreviewPanel   (Panel 2: results + KB destination)
 *   - OpsRetrievalFallbackDialog (shared sub-component)
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type OpsAdvancedQuery } from '../services/opsRetrievalService';
import { subProjectService, type CpcSuggestion, type CpcSubclassSuggestion } from '../services/subProjectService';
import { EMPTY_QUERY } from './OpsRetrievalFormParts';
import OpsRetrievalCriteriaPanel from './OpsRetrievalCriteriaPanel';
import OpsRetrievalPreviewPanel from './OpsRetrievalPreviewPanel';
import OpsRetrievalFallbackDialog from './OpsRetrievalFallbackDialog';
import { useOpsRetrievalJob } from '../hooks/useOpsRetrievalJob';

interface OpsRetrievalModalProps {
  subProjectId: string;
  onClose: () => void;
}

type Panel = 'criteria' | 'preview';
type CpcPhase = 'idle' | 'loop1_loading' | 'loop2_loading' | 'complete' | 'error';

export default function OpsRetrievalModal({ subProjectId, onClose }: OpsRetrievalModalProps) {
  const { t } = useTranslation();

  // ── Criteria-panel state (specific to this modal) ────────────────────────────
  const [panel, setPanel] = useState<Panel>('criteria');
  const [query, setQuery] = useState<OpsAdvancedQuery>(EMPTY_QUERY);
  const [showCql, setShowCql] = useState(false);

  // CPC suggestion state — two-phase
  const [cpcPhase, setCpcPhase] = useState<CpcPhase>('idle');
  const [cpcSubclasses, setCpcSubclasses] = useState<CpcSubclassSuggestion[]>([]);
  const [cpcSuggestions, setCpcSuggestions] = useState<CpcSuggestion[]>([]);
  const [cpcError, setCpcError] = useState<string | null>(null);
  const [selectedCpcCodes, setSelectedCpcCodes] = useState<Set<string>>(new Set());

  // ── Shared job logic ─────────────────────────────────────────────────────────
  const job = useOpsRetrievalJob(subProjectId);

  // ── CPC suggestions: Loop 1 then auto-trigger Loop 2 ────────────────────────
  useEffect(() => {
    let cancelled = false;
    setCpcPhase('loop1_loading');
    setCpcError(null);

    subProjectService.suggestCpc(subProjectId)
      .then((subclasses) => {
        if (cancelled) return;
        setCpcSubclasses(subclasses);
        setCpcPhase('loop2_loading');
        return subProjectService.refineCpcSuggestions(subProjectId, subclasses.map((s) => s.code));
      })
      .then((suggestions) => {
        if (cancelled || !suggestions) return;
        setCpcSuggestions(suggestions);
        setCpcPhase('complete');
      })
      .catch((err) => {
        if (cancelled) return;
        setCpcError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load CPC suggestions');
        setCpcPhase('error');
      });

    return () => { cancelled = true; };
  }, [subProjectId]);

  // Sync selected CPC codes into query.cpc
  useEffect(() => {
    setQuery((q) => ({ ...q, cpc: Array.from(selectedCpcCodes) }));
  }, [selectedCpcCodes]);

  const toggleCpc = useCallback((code: string) => {
    setSelectedCpcCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  const selectAllCpc = useCallback(() => {
    setSelectedCpcCodes(new Set(cpcSuggestions.map((s) => s.code)));
  }, [cpcSuggestions]);

  const deselectAllCpc = useCallback(() => {
    setSelectedCpcCodes(new Set());
  }, []);

  // ── Field updaters (memoised for CriteriaPanel) ──────────────────────────────
  const set = useCallback(<K extends keyof OpsAdvancedQuery>(key: K, value: OpsAdvancedQuery[K]) => {
    setQuery((q) => ({ ...q, [key]: value }));
  }, []);

  const toggleCountry = useCallback((code: string) => {
    setQuery((q) => {
      const current = q.country_codes ?? [];
      return {
        ...q,
        country_codes: current.includes(code)
          ? current.filter((c) => c !== code)
          : [...current, code],
      };
    });
  }, []);

  const hasAnyCriteria = !!(
    query.keywords_title_abstract || query.keywords_title || query.keywords_full_text ||
    (query.inventor?.length ?? 0) > 0 || (query.applicant?.length ?? 0) > 0 ||
    (query.cpc?.length ?? 0) > 0 || (query.ipc?.length ?? 0) > 0 ||
    query.publication_number || query.application_number || query.priority_number ||
    query.publication_date_from || query.publication_date_to ||
    query.application_date_from || query.application_date_to ||
    query.priority_date_from || query.priority_date_to
  );

  // ── Preview trigger ──────────────────────────────────────────────────────────
  const handlePreview = async () => {
    await job.startPreview(query);
    setPanel('preview');
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-screen-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              {t('opsRetrieval.title', 'EP Patent Retrieval — Direct Query')}
            </h2>
            {panel === 'preview' && (
              <p className="text-xs text-slate-500">
                {t('opsRetrieval.preview.subtitle', 'Sélectionnez la base de connaissances de destination')}
              </p>
            )}
          </div>
          <span
            className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer leading-none"
            onClick={onClose}
          >
            &times;
          </span>
        </div>

        {panel === 'criteria' ? (
          <OpsRetrievalCriteriaPanel
            query={query}
            set={set}
            toggleCountry={toggleCountry}
            showCql={showCql}
            setShowCql={setShowCql}
            hasAnyCriteria={hasAnyCriteria}
            previewing={job.previewing}
            previewError={job.previewError}
            onClose={onClose}
            onPreview={handlePreview}
            cpcPhase={cpcPhase}
            cpcSubclasses={cpcSubclasses}
            cpcSuggestions={cpcSuggestions}
            cpcError={cpcError}
            selectedCpcCodes={selectedCpcCodes}
            onToggleCpc={toggleCpc}
            onSelectAllCpc={selectAllCpc}
            onDeselectAllCpc={deselectAllCpc}
          />
        ) : job.previewResult ? (
          <OpsRetrievalPreviewPanel
            previewResult={job.previewResult}
            samplesLoading={job.samplesLoading}
            samplesError={job.samplesError}
            kbs={job.kbs}
            kbsLoading={job.kbsLoading}
            selectedKbId={job.selectedKbId}
            setSelectedKbId={job.setSelectedKbId}
            newKbName={job.newKbName}
            setNewKbName={job.setNewKbName}
            kbReady={job.kbReady}
            launching={job.launching || job.testingGooglePatents}
            launchError={job.launchError}
            onModify={() => {
              setPanel('criteria');
              job.resetPreview();
            }}
            onLaunch={(mode, limit) => job.handleLaunch(mode, query, limit)}
          />
        ) : null}

        {job.testingGooglePatents && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg z-10">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              {t('opsRetrieval.testingGooglePatents', 'Testing fast download...')}
            </div>
          </div>
        )}
      </div>

      {job.showFallbackDialog && job.previewResult && (
        <OpsRetrievalFallbackDialog
          estimatedFullSeconds={job.previewResult.estimated_full_seconds}
          onConfirm={() => job.handleFallbackConfirm(query)}
          onCancel={() => job.setShowFallbackDialog(false)}
        />
      )}
    </div>
  );
}
